import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Upload, Sparkles, Loader2, CheckCircle, XCircle, Trash2, Users } from 'lucide-react';
import API from '../lib/api';

export default function BatchWorkersPage() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [extractedWorkers, setExtractedWorkers] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [extractProgress, setExtractProgress] = useState(0);
  const [creating, setCreating] = useState(false);
  const [results, setResults] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  // Config pattern
  const [emailDomain, setEmailDomain] = useState('');
  const [defaultPassword, setDefaultPassword] = useState('Temp1234!');

  const generateEmail = (name, domain) => {
    if (!name || !domain) return '';
    const clean = name.toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z\s]/g, '')
      .trim();
    const parts = clean.split(/\s+/);
    if (parts.length >= 2) {
      return `${parts[0]}.${parts[parts.length - 1]}@${domain}`;
    }
    return `${parts[0]}@${domain}`;
  };

  const handleFilesSelected = (newFiles) => {
    const validFiles = Array.from(newFiles).filter(f => {
      const ext = f.name.split('.').pop().toLowerCase();
      return ['jpg', 'jpeg', 'png', 'webp', 'pdf'].includes(ext);
    });
    setFiles(prev => [...prev, ...validFiles]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFilesSelected(e.dataTransfer.files);
  };

  const removeFile = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const extractAll = async () => {
    if (files.length === 0) return;
    setExtracting(true);
    setExtractProgress(0);
    const results = [];

    for (let i = 0; i < files.length; i++) {
      try {
        const fd = new FormData();
        fd.append('file', files[i]);
        const res = await API.post('/workers/extract-from-document', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const data = res.data.extracted || {};
        // Auto-generate email if domain set
        const email = emailDomain ? generateEmail(data.name || '', emailDomain) : '';
        results.push({
          ...data,
          email,
          password: defaultPassword,
          _source: files[i].name,
          _valid: true
        });
      } catch (err) {
        results.push({
          name: '',
          email: '',
          password: defaultPassword,
          _source: files[i].name,
          _valid: false,
          _error: 'Error al extraer datos'
        });
      }
      setExtractProgress(i + 1);
    }

    setExtractedWorkers(results);
    setExtracting(false);
  };

  const updateWorker = (idx, field, value) => {
    setExtractedWorkers(prev => prev.map((w, i) => i === idx ? { ...w, [field]: value } : w));
  };

  const removeWorker = (idx) => {
    setExtractedWorkers(prev => prev.filter((_, i) => i !== idx));
  };

  const createAll = async () => {
    const validWorkers = extractedWorkers.filter(w => w.name && w.email && w.password);
    if (validWorkers.length === 0) return;
    
    setCreating(true);
    try {
      const res = await API.post('/workers/batch', validWorkers);
      setResults(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  // Results screen
  if (results) {
    return (
      <div className="space-y-6" data-testid="batch-results">
        <button onClick={() => navigate('/company/workers')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#FF6B35]">
          <ArrowLeft className="w-4 h-4" /> Volver a trabajadores
        </button>
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center shadow-sm">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Importacion completada</h2>
          <p className="text-gray-500 mb-6">
            {results.total_created} trabajadores creados
            {results.total_errors > 0 && `, ${results.total_errors} errores`}
          </p>
          {results.errors?.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 text-left max-w-md mx-auto">
              <p className="text-sm font-medium text-red-800 mb-2">Errores:</p>
              {results.errors.map((e, i) => (
                <p key={i} className="text-xs text-red-600">{e.name}: {e.error}</p>
              ))}
            </div>
          )}
          <Button onClick={() => navigate('/company/workers')} className="bg-[#FF6B35] hover:bg-[#E55A2B] text-white">
            Ver trabajadores
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="batch-workers-page">
      <button onClick={() => navigate('/company/workers')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#FF6B35]">
        <ArrowLeft className="w-4 h-4" /> Volver a trabajadores
      </button>

      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-gray-900">Importacion masiva con IA</h1>
        <p className="text-sm text-gray-500 mt-0.5">Sube multiples documentos y crea trabajadores automaticamente</p>
      </div>

      {extractedWorkers.length === 0 ? (
        <>
          {/* Config */}
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Configuracion de importacion</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-gray-600">Dominio de email (para auto-generar)</Label>
                <Input
                  value={emailDomain}
                  onChange={(e) => setEmailDomain(e.target.value)}
                  placeholder="miempresa.com"
                  data-testid="email-domain-input"
                />
                <p className="text-xs text-gray-400">Se generara: nombre.apellido@dominio</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-gray-600">Contraseña por defecto</Label>
                <Input
                  value={defaultPassword}
                  onChange={(e) => setDefaultPassword(e.target.value)}
                  placeholder="Contraseña temporal"
                  data-testid="default-password-input"
                />
                <p className="text-xs text-gray-400">Los trabajadores podran cambiarla despues</p>
              </div>
            </div>
          </div>

          {/* Upload Zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
              dragOver ? 'border-[#FF6B35] bg-orange-50' : 'border-gray-200 bg-white hover:border-orange-300'
            }`}
          >
            <input
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.pdf"
              multiple
              onChange={(e) => { handleFilesSelected(e.target.files); e.target.value = ''; }}
              className="hidden"
              id="batch-upload"
              data-testid="batch-upload-input"
            />
            <label htmlFor="batch-upload" className="cursor-pointer block">
              <Upload className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-700 mb-1">Arrastra o selecciona documentos</p>
              <p className="text-xs text-gray-500">DNIs, contratos, nominas... (JPG, PNG, PDF) - Multiples archivos</p>
            </label>
          </div>

          {/* File list */}
          {files.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-gray-900">{files.length} documento(s) seleccionados</p>
                <Button
                  onClick={extractAll}
                  disabled={extracting}
                  className="bg-[#FF6B35] hover:bg-[#E55A2B] text-white"
                  data-testid="extract-all-btn"
                >
                  {extracting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Extrayendo ({extractProgress}/{files.length})
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Extraer datos con IA
                    </>
                  )}
                </Button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-700 truncate">{f.name}</span>
                    <button onClick={() => removeFile(i)} className="text-xs text-red-500 hover:text-red-700 ml-2">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        /* Preview Table */
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Revision de datos extraidos</h3>
              <p className="text-xs text-gray-500">{extractedWorkers.length} trabajadores detectados — Revisa y corrige antes de crear</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => { setExtractedWorkers([]); setFiles([]); }}>
                Cancelar
              </Button>
              <Button
                onClick={createAll}
                disabled={creating}
                className="bg-[#FF6B35] hover:bg-[#E55A2B] text-white"
                data-testid="create-all-btn"
              >
                {creating ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creando...</>
                ) : (
                  <><Users className="w-4 h-4 mr-2" /> Crear todos ({extractedWorkers.filter(w => w.name && w.email).length})</>
                )}
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Fuente</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Nombre</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Email</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">DNI</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Puesto</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Estado</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {extractedWorkers.map((w, i) => (
                  <tr key={i} className="hover:bg-gray-50/50">
                    <td className="px-3 py-2">
                      <span className="text-xs text-gray-400 truncate block max-w-[100px]">{w._source}</span>
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        value={w.name || ''}
                        onChange={(e) => updateWorker(i, 'name', e.target.value)}
                        className="h-7 text-xs border-gray-200"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        value={w.email || ''}
                        onChange={(e) => updateWorker(i, 'email', e.target.value)}
                        className="h-7 text-xs border-gray-200"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        value={w.dni || ''}
                        onChange={(e) => updateWorker(i, 'dni', e.target.value)}
                        className="h-7 text-xs border-gray-200 w-28"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        value={w.position || ''}
                        onChange={(e) => updateWorker(i, 'position', e.target.value)}
                        className="h-7 text-xs border-gray-200"
                      />
                    </td>
                    <td className="px-3 py-2">
                      {w._valid === false ? (
                        <Badge className="bg-red-50 text-red-600 text-xs">Error</Badge>
                      ) : w.name && w.email ? (
                        <Badge className="bg-green-50 text-green-600 text-xs">Listo</Badge>
                      ) : (
                        <Badge className="bg-amber-50 text-amber-600 text-xs">Incompleto</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <button onClick={() => removeWorker(i)} className="text-red-400 hover:text-red-600">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
